/* ==========================================================================
   Scan sequence — plays the readout the Liquid already rendered finished.

   Order of operations matters more than the animation does: the section is
   correct in the HTML, this file rewinds it, plays once, and lands back on
   exactly the state it started from. Nothing is built by JavaScript, so a
   failure here costs the animation and not the section.

   Only transform and opacity are animated (the fault pop and the reading
   pulse, both in CSS). Everything else is a data-state swap, so five rows
   changing colour is five attribute writes, not five paint transitions.

   prefers-reduced-motion: the sequence never starts.
   ========================================================================== */
(() => {
  'use strict';

  const fill = (s, vars) => String(s).replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => (vars[k] == null ? '' : vars[k]));

  function play(root, t) {
    const rows = [...root.querySelectorAll('[data-gp-scan-row]')];
    if (!rows.length) return;

    const per = Math.max(120, (parseInt(root.dataset.gpMs, 10) || 500) / 2);
    const total = rows.length;

    const busy = root.querySelector('[data-gp-scan-busy]');
    const result = root.querySelector('[data-gp-scan-result]');
    const big = root.querySelector('[data-gp-scan-big]');
    const bigLabel = root.querySelector('[data-gp-scan-big-label]');
    const summary = root.querySelector('[data-gp-scan-summary]');
    const replay = root.parentElement.querySelector('[data-gp-scan-replay]');

    // Remember the finished state so the sequence lands back on it exactly,
    // rather than on a second copy of it built here.
    const done = rows.map((r) => r.dataset.state);
    const doneStatus = rows.map((r) => r.querySelector('[data-gp-scan-status]').textContent);
    const doneBusy = busy ? busy.textContent : '';
    const doneBig = big ? big.textContent : '';
    const doneLabel = bigLabel ? bigLabel.textContent : '';
    const doneSummary = summary ? summary.textContent : '';

    let timer = null;

    const paint = (tick) => {
      rows.forEach((row, i) => {
        const status = row.querySelector('[data-gp-scan-status]');
        if (tick >= i * 2 + 2) {
          row.dataset.state = done[i];
          status.textContent = doneStatus[i];
        } else if (tick >= i * 2) {
          row.dataset.state = 'reading';
          status.textContent = t.scan_reading;
        } else {
          row.dataset.state = 'idle';
          status.textContent = t.scan_waiting;
        }
      });

      const read = Math.min(total, Math.floor(tick / 2));
      const finished = tick >= total * 2;

      if (finished) {
        if (busy) busy.textContent = doneBusy;
        if (result) result.dataset.state = 'done';
        if (big) big.textContent = doneBig;
        if (bigLabel) bigLabel.textContent = doneLabel;
        if (summary) summary.textContent = doneSummary;
        if (replay) { replay.textContent = t.scan_replay; replay.disabled = false; }
        return;
      }

      const next = rows[Math.min(read, total - 1)];
      const code = next ? next.querySelector('.gp-scan__code').textContent.trim() : '';
      if (busy) busy.textContent = fill(t.scan_querying, { code });
      if (result) result.dataset.state = 'running';
      if (big) big.textContent = t.scan_dash;
      if (bigLabel) bigLabel.textContent = t.scan_big_pending;
      if (summary) summary.textContent = fill(t.scan_progress, { read, total });
      if (replay) { replay.textContent = t.scan_busy; replay.disabled = true; }
    };

    const start = () => {
      if (timer) clearInterval(timer);
      let tick = 0;
      paint(tick);
      timer = setInterval(() => {
        tick += 1;
        paint(tick);
        if (tick >= total * 2) { clearInterval(timer); timer = null; }
      }, per);
    };

    if (replay) {
      replay.hidden = false;
      replay.addEventListener('click', start);
    }

    if ('IntersectionObserver' in window) {
      let played = false;
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !played) { played = true; io.disconnect(); start(); }
        });
      }, { threshold: 0.35 });
      io.observe(root);
    } else {
      start();
    }
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-gp-scan]').forEach((root) => {
      if (root.dataset.gpScanReady) return;
      root.dataset.gpScanReady = '1';

      const node = root.querySelector('[data-gp-scan-strings]');
      if (!node) return;
      let t;
      try { t = JSON.parse(node.textContent); } catch { return; }

      // The finished state is already on the page. A visitor who has asked for
      // less motion keeps it, and the replay button stays hidden — there is
      // nothing to replay.
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      play(root, t);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
